import { useState, useRef, useEffect } from "react"
import { Sidebar } from "../components/ui/sidebar"
import { AIChatHistorySidebar } from "../components/ui/ai-companion/ai-chat-history-sidebar"
import { ChatMessagesContainer } from "../components/ui/ai-companion/chat-messages-container"
import {AIMessage} from "../components/ui/ai-companion/ai-message"
import {UserMessage} from "../components/ui/ai-companion/user-message"
import {ChatInput} from "../components/ui/ai-companion/chat-input"
import {AIPreviewSection} from "../components/ui/ai-companion/ai-preview-section"
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute"
import { UniversalSearch } from "../components/ui/universal-search"

const demoMessages = {
  "1": [
    {
      id: "1",
      type: "ai",
      content:
        "Hi Alex! 👋 I'm ready to help you learn today. We can practice math, write a story, or explore science facts. What would you like to do?",
    },
    {
      id: "2",
      type: "user",
      content: "Can you tell me interesting facts about the Solar System? I need to make a poster for class.",
    },
    {
      id: "3",
      type: "ai",
      content:
        "Here are three amazing facts about our Solar System for your poster:\n\n• Jupiter is huge! You could fit 1,300 Earths inside Jupiter.\n• Venus spins backwards. It's the only planet that spins clockwise.\n• Mars has the tallest mountain. Olympus Mons is 3 times taller than Everest!\n\nI've also generated a visual diagram of the planets for you. Check the preview panel!",
    },
  ],
  "2": [
    {
      id: "1",
      type: "ai",
      content: "Hi! I can help you with fractions. Let's start with the basics. What's your current level?",
    },
    {
      id: "2",
      type: "user",
      content: "I'm confused about how to add fractions with different denominators.",
    },
    {
      id: "3",
      type: "ai",
      content:
        "Great question! When adding fractions with different denominators, you need to find a common denominator first. For example, 1/2 + 1/3 becomes 3/6 + 2/6 = 5/6. Would you like to try some practice problems?",
    },
  ],
}

const AICompanionPage = () => {
  const [messages, setMessages] = useState(demoMessages["1"])
  const [activeChat, setActiveChat] = useState("1")
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [viewMode, setViewMode] = useState("chat")
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSelectChat = (chatId) => {
    console.log(" Loading chat:", chatId)
    setActiveChat(chatId)
    setMessages(demoMessages[chatId] || demoMessages["1"])
  }

  const handleDeleteChat = (chatId) => {
    console.log(" Chat deleted:", chatId)
    // In a real app, this would delete the chat from the database
  }

  const handleSendMessage = (content) => {
    const userMessage = {
      id: Date.now().toString(),
      type: "user",
      content,
    }

    setMessages((prev) => [...prev, userMessage])
    console.log(" Message sent:", content)

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content:
          "That's an excellent question! Let me help you understand this better. Based on what you've asked, here are some key points to remember: First, practice makes perfect! Second, don't hesitate to ask for help. And finally, every expert was once a beginner. Would you like me to explain more or show you a visual example?",
      }
      setMessages((prev) => [...prev, aiResponse])
    }, 1000)
  }

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-50">
        {/* Main Sidebar Navigation - Fixed position */}
        <div className="fixed left-0 top-0 h-full z-40">
          <Sidebar />
        </div>

<UniversalSearch/>
        <AIChatBox />

        {/* Main container with margin for the fixed sidebar */}
        <div className="flex flex-1 ml-[var(--sidebar-width,240px)] min-h-screen">
          {/* Chat History Sidebar - Now positioned relative to main sidebar */}
          <AIChatHistorySidebar
            isCollapsed={isSidebarCollapsed}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onSelectChat={handleSelectChat}
            onDeleteChat={handleDeleteChat}
            activeChat={activeChat}
          />

          {/* Main Content */}
          <main className="flex-1 flex flex-col">
            {/* Header with View Toggle */}
            <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-6">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    EarlyScape AI Companion
                    <span className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full font-bold">BETA</span>
                  </h2>
                  <p className="text-xs text-slate-500">Your personal learning assistant</p>
                </div>
              </div>

              {/* View Toggle Buttons */}
              {/* <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                <button
                  onClick={() => {
                    console.log(" Switched to chat view")
                    setViewMode("chat")
                    setIsPreviewFullscreen(false)
                  }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                    viewMode === "chat" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Chat
                </button>
                <button
                  onClick={() => {
                    console.log(" Switched to preview view")
                    setViewMode("preview")
                    setIsPreviewFullscreen(true)
                  }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                    viewMode === "preview" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Preview
                </button>
              </div> */}
            </header>

            {/* Content Area */}
            <div className={`flex-1 flex overflow-hidden ${viewMode === "preview" ? "hidden xl:flex" : "flex"}`}>
              {/* Chat Section */}
              {viewMode === "chat" && (
                <section className="flex-1 flex flex-col max-w-4xl mx-auto w-full">
                  <ChatMessagesContainer>
                    {messages.map((message) =>
                      message.type === "ai" ? (
                        <AIMessage key={message.id} content={message.content} />
                      ) : (
                        <UserMessage key={message.id} content={message.content} />
                      ),
                    )}
                    <div ref={messagesEndRef} />
                  </ChatMessagesContainer>

                  <div className="p-6">
                    <ChatInput onSend={handleSendMessage} />
                    <p className="text-center text-xs text-slate-400 mt-3">
                      EarlyScape AI can make mistakes. Check important info.
                    </p>
                  </div>
                </section>
              )}

              {/* Preview Section */}
              <AIPreviewSection
                isFullscreen={isPreviewFullscreen}
                onToggleFullscreen={() => setIsPreviewFullscreen(!isPreviewFullscreen)}
              />
            </div>

            {/* Preview Only View */}
            {viewMode === "preview" && (
              <section className="flex-1 flex flex-col">
                <AIPreviewSection
                  isFullscreen={true}
                  onToggleFullscreen={() => {
                    setViewMode("chat")
                    setIsPreviewFullscreen(false)
                  }}
                />
              </section>
            )}
          </main>
        </div>
      </div>
    </ProtectedRoute>
  )
}

export default AICompanionPage